import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit, Trash2, Eye, Heart } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { blogAPI, type BlogArticle } from '../services/api';
import { formatMsk } from '../utils/msk';

const BlogListPage: React.FC = () => {
  const [articles, setArticles] = useState<BlogArticle[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      const { articles: list } = await blogAPI.list();
      setArticles(list || []);
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'Ошибка загрузки статей');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number, title: string) => {
    if (!window.confirm(`Удалить статью «${title}»?`)) return;
    try {
      await blogAPI.delete(id);
      toast.success('Статья удалена');
      load();
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'Ошибка удаления');
    }
  };

  const formatDate = (s: string | null | undefined) => {
    if (!s) return '—';
    try {
      return new Date(s).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return s;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Блог</h1>
        <Link
          to="/blog/new"
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
        >
          <Plus className="h-5 w-5" />
          Добавить статью
        </Link>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {articles.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            Статей пока нет. Нажмите «Добавить статью», чтобы создать первую.
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Заголовок</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Slug</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Автор</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Дата</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Статус</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Просмотры / Лайки</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Действия</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {articles.map((a) => (
                <tr key={a.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{a.title}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 font-mono">{a.slug}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{a.authorName || '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatDate(a.publishedAt)}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap ${
                        a.status === 'published'
                          ? 'bg-green-100 text-green-800'
                          : a.status === 'scheduled'
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {a.status === 'published'
                        ? 'Опубликовано'
                        : a.status === 'scheduled'
                          ? `Запланирована · ${formatMsk(a.scheduledPublishAt)}`
                          : 'Черновик'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 flex items-center gap-3">
                    <span className="flex items-center gap-1">
                      <Eye className="h-4 w-4" />
                      {a.viewsCount ?? 0}
                    </span>
                    <span className="flex items-center gap-1">
                      <Heart className="h-4 w-4" />
                      {a.likesCount ?? 0}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      to={`/blog/${a.id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded"
                    >
                      <Edit className="h-4 w-4" />
                      Редактировать
                    </Link>
                    <button
                      type="button"
                      onClick={() => a.id && handleDelete(a.id, a.title)}
                      className="inline-flex items-center gap-1 px-2 py-1 text-sm text-red-600 hover:bg-red-50 rounded ml-2"
                    >
                      <Trash2 className="h-4 w-4" />
                      Удалить
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default BlogListPage;
